import { Link } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import CategoryDropdown from '@/components/feature/CategoryDropdown';
import CategoryCardGrid from '@/components/feature/CategoryCardGrid';
import { categories, mainSiteUrl } from '@/mocks/categories';

export default function Home() {
  const fadeRefs = useRef<(HTMLElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
            entry.target.classList.remove('opacity-0', 'translate-y-6');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -60px 0px' }
    );

    fadeRefs.current.forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <Navbar />

      {/* Hero Section */}
      <section className="relative w-full min-h-[85vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Stylized%20artistic%20composition%20of%20fashion%20accessories%20sneakers%20bags%20and%20apparel%20arranged%20in%20an%20editorial%20flat%20lay%20on%20a%20warm%20sand%20colored%20surface%20soft%20diffused%20daylight%20minimal%20shadows%20contemporary%20product%20photography%20aesthetic%20clean%20elegant%20neutral%20palette%20with%20subtle%20textural%20elements&width=1800&height=1200&seq=hero-bg-v2-2026&orientation=landscape"
            alt="Sugargoo Spreadsheet product collection"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/50"></div>
        </div>

        <div className="relative z-10 w-full px-6 md:px-10 py-24 md:py-32 flex flex-col items-center text-center">
          <div className="max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-background-50/15 backdrop-blur-sm rounded-full border border-background-50/20 mb-6">
              <i className="ri-sparkling-line text-sm text-accent-400"></i>
              <span className="text-xs text-background-50/90 font-medium tracking-wide">
                UPDATED 2026 &bull; INDEPENDENT RESOURCE
              </span>
            </div>

            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-background-50 leading-tight mb-5">
              The Ultimate <span className="text-accent-400">Sugargoo Spreadsheet</span> Database
            </h1>

            <p className="text-base md:text-lg text-background-50/80 max-w-2xl mx-auto leading-relaxed mb-8">
              Discover the latest Sugargoo Spreadsheet links, product collections, QC guides,
              shipping information and expert shopping tips — all organized in one powerful resource.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4 mb-10">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="w-full sm:w-auto px-8 py-3.5 bg-accent-500 text-foreground-950 rounded-full font-semibold text-sm hover:bg-accent-400 transition-all duration-200 whitespace-nowrap cursor-pointer shadow-lg shadow-accent-500/20"
              >
                Sugargoo Spreadsheet
                <i className="ri-arrow-right-up-line ml-2"></i>
              </a>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="w-full sm:w-auto px-8 py-3.5 bg-background-50/15 backdrop-blur-sm border border-background-50/30 text-background-50 rounded-full font-semibold text-sm hover:bg-background-50/25 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Buy It Now
                <i className="ri-shopping-cart-line ml-2"></i>
              </a>
            </div>

            {/* Trust Badges */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 max-w-2xl mx-auto">
              {[
                { icon: 'ri-check-double-line', text: 'Updated Resources' },
                { icon: 'ri-search-eye-line', text: 'Independent Research' },
                { icon: 'ri-global-line', text: 'Global Shopping Guide' },
                { icon: 'ri-user-heart-line', text: 'Beginner Friendly' },
              ].map((badge, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 px-3 py-2 bg-background-50/10 backdrop-blur-sm rounded-lg border border-background-50/15"
                >
                  <i className={`${badge.icon} text-sm text-accent-400`}></i>
                  <span className="text-xs text-background-50/80 whitespace-nowrap">{badge.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2">
          <span className="text-xs text-background-50/50 tracking-wider">SCROLL</span>
          <div className="w-5 h-8 rounded-full border border-background-50/30 flex items-start justify-center p-1">
            <div className="w-1 h-2 rounded-full bg-background-50/50 animate-bounce"></div>
          </div>
        </div>
      </section>

      {/* Category Dropdown Section */}
      <section
        id="categories"
        ref={(el) => { fadeRefs.current[0] = el; }}
        className="w-full px-6 md:px-10 py-16 md:py-20 bg-background-100/50 opacity-0 translate-y-6 transition-all duration-700 ease-out"
      >
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
            Browse by Category
          </h2>
          <p className="text-sm md:text-base text-foreground-600 mb-8 max-w-xl mx-auto">
            Select a category below to explore curated product spreadsheets on our main platform.
          </p>
          <CategoryDropdown />
        </div>
      </section>

      {/* Category Cards Grid Section */}
      <section className="w-full px-6 md:px-10 py-16 md:py-20 bg-background-50">
        <div className="max-w-6xl mx-auto">
          <div
            ref={(el) => { fadeRefs.current[1] = el; }}
            className="text-center mb-10 opacity-0 translate-y-6 transition-all duration-700 ease-out"
          >
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              Explore All Product Categories
            </h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
              Each category links directly to our spreadsheet database with verified finds, prices and seller ratings.
            </p>
          </div>
          <div
            ref={(el) => { fadeRefs.current[2] = el; }}
            className="opacity-0 translate-y-6 transition-all duration-700 ease-out delay-100"
            data-product-shop
          >
            <CategoryCardGrid />
          </div>
        </div>
      </section>

      {/* Why Sugargoo Spreadsheet Section */}
      <section
        id="guide"
        ref={(el) => { fadeRefs.current[3] = el; }}
        className="w-full px-6 md:px-10 py-16 md:py-20 bg-background-100/50 opacity-0 translate-y-6 transition-all duration-700 ease-out"
      >
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              Why Use a Sugargoo Spreadsheet?
            </h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
              Compare the difference between searching manually and using an organized spreadsheet.
            </p>
          </div>

          <div className="overflow-x-auto rounded-xl border border-background-200/70 bg-background-50">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-200/70">
                  <th className="text-left px-5 py-4 font-heading font-semibold text-foreground-900">Feature</th>
                  <th className="text-left px-5 py-4 font-heading font-semibold text-foreground-600">
                    <i className="ri-search-line mr-1.5"></i> Manual Searching
                  </th>
                  <th className="text-left px-5 py-4 font-heading font-semibold text-primary-600">
                    <i className="ri-file-list-3-line mr-1.5"></i> Spreadsheet
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200/50">
                {[
                  { feature: 'Time Required', manual: 'Hours of browsing', sheet: 'Browse in minutes' },
                  { feature: 'Organization', manual: 'Scattered links', sheet: 'Structured categories' },
                  { feature: 'Product Discovery', manual: 'Limited exposure', sheet: 'Curated collections' },
                  { feature: 'Seller Comparison', manual: 'Manual research', sheet: 'Side-by-side ratings' },
                  { feature: 'QC Research', manual: 'Search individually', sheet: 'Aggregated QC data' },
                  { feature: 'Beginner Friendly', manual: 'Steep learning curve', sheet: 'Guided experience' },
                ].map((row, i) => (
                  <tr key={i} className="hover:bg-background-50 transition-colors">
                    <td className="px-5 py-3.5 font-medium text-foreground-800 whitespace-nowrap">{row.feature}</td>
                    <td className="px-5 py-3.5 text-foreground-500">{row.manual}</td>
                    <td className="px-5 py-3.5 text-primary-600 font-medium">{row.sheet}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* SEO Article Section */}
      <section
        id="articles"
        ref={(el) => { fadeRefs.current[4] = el; }}
        className="w-full px-6 md:px-10 py-16 md:py-20 bg-background-50 opacity-0 translate-y-6 transition-all duration-700 ease-out"
      >
        <div className="max-w-4xl mx-auto">
          <article>
            <header className="mb-10 text-center">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-secondary-100 rounded-full mb-4">
                <span className="text-xs text-secondary-700 font-medium">FEATURED ARTICLE</span>
              </div>
              <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
                How a Sugargoo Spreadsheet Transforms Your International Shopping Experience
              </h2>
              <p className="text-sm text-foreground-500">
                Updated August 2026 &bull; 8 min read
              </p>
            </header>

            <div className="prose-custom space-y-6">
              <section>
                <h3 className="font-heading text-xl font-semibold text-foreground-900 mb-3">
                  The Rise of Organized Product Discovery
                </h3>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  International shopping through agent platforms has grown exponentially over the past few years.
                  Shoppers from Europe, North America and across the globe are constantly searching for reliable
                  product links, verified sellers and honest quality assessments. A well-maintained
                  <strong>Sugargoo Spreadsheet</strong> serves as the backbone of this discovery process,
                  transforming scattered information into a structured, searchable database that saves
                  countless hours of manual browsing.
                </p>
              </section>

              <section>
                <h3 className="font-heading text-xl font-semibold text-foreground-900 mb-3">
                  What Makes a Great Sugargoo Product Spreadsheet?
                </h3>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  Not all spreadsheets are created equal. The most effective
                  <strong>Sugargoo Spreadsheet</strong> resources share several key characteristics
                  that elevate them above simple link collections. They categorize products
                  logically — shoes grouped separately from clothing, accessories organized by
                  type, and electronics clearly labeled with specifications. Each entry includes
                  not just the product link but also the current price range, seller rating,
                  available sizes, and any quality control notes from previous buyers.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                  {[
                    { icon: 'ri-price-tag-3-line', title: 'Price Tracking', desc: 'Monitor price changes across sellers and seasons to find the best deals on Sugargoo products.' },
                    { icon: 'ri-shield-check-line', title: 'QC Verification', desc: 'Access aggregated quality control reports so you know exactly what to expect before ordering.' },
                    { icon: 'ri-group-line', title: 'Community Reviews', desc: 'Benefit from real buyer experiences and honest feedback on product quality and seller reliability.' },
                    { icon: 'ri-refresh-line', title: 'Regular Updates', desc: 'The best spreadsheets are updated frequently with new finds, discontinued items removed and prices refreshed.' },
                  ].map((item, i) => (
                    <div key={i} className="flex gap-3 p-4 rounded-xl bg-background-100/80 border border-background-200/50">
                      <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className={`${item.icon} text-base text-primary-600`}></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm text-foreground-900 mb-1">{item.title}</h4>
                        <p className="text-xs text-foreground-600 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h3 className="font-heading text-xl font-semibold text-foreground-900 mb-3">
                  Categories That Cover Everything You Need
                </h3>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  A comprehensive <strong>Sugargoo Spreadsheet</strong> covers fourteen distinct
                  product categories, from footwear and streetwear to accessories and lifestyle
                  items. Shoppers looking for the latest sneaker releases can navigate directly
                  to the Shoes section, while those hunting for designer-inspired bags, belts
                  or fragrances have dedicated categories with curated selections.
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mt-3">
                  The category system ensures that whether you are searching for
                  <strong>hoodies and sweaters</strong> for the winter season, <strong>jerseys</strong> for
                  sports enthusiasts, or <strong>underwear and underpants</strong> for everyday essentials,
                  every product type has its own organized space. This structure eliminates the
                  frustration of endless scrolling through mixed product feeds and allows for
                  focused, efficient browsing.
                </p>
              </section>

              <section>
                <h3 className="font-heading text-xl font-semibold text-foreground-900 mb-3">
                  Getting Started with Sugargoo Spreadsheet Shopping
                </h3>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  If you are new to agent-based international shopping, starting with a
                  <strong>Sugargoo Spreadsheet</strong> is the most efficient path forward.
                  Rather than spending days learning which sellers are trustworthy and which
                  products offer genuine value, you can leverage the collective research of
                  experienced shoppers who have already done the groundwork. Begin by browsing
                  the category that interests you most, compare a few products within that
                  category, check the QC notes and community ratings, and make your first
                  informed purchase with confidence.
                </p>
              </section>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-6">
                <Link
                  to="/article/sugargoo-spreadsheet-guide"
                  className="w-full sm:w-auto px-6 py-3 bg-primary-500 text-background-50 rounded-full font-semibold text-sm hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer text-center"
                >
                  Read Complete Guide
                  <i className="ri-arrow-right-line ml-2"></i>
                </Link>
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="w-full sm:w-auto px-6 py-3 bg-background-100 border border-background-300 text-foreground-800 rounded-full font-semibold text-sm hover:bg-background-200 transition-all duration-200 whitespace-nowrap cursor-pointer text-center"
                >
                  Browse Main Site
                  <i className="ri-external-link-line ml-2"></i>
                </a>
              </div>
            </div>
          </article>
        </div>
      </section>

      {/* 9 SEO Article Cards Grid */}
      <section
        ref={(el) => { fadeRefs.current[5] = el; }}
        className="w-full px-6 md:px-10 py-16 md:py-20 bg-background-100/50 opacity-0 translate-y-6 transition-all duration-700 ease-out"
      >
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-accent-100 rounded-full mb-4">
              <i className="ri-article-line text-sm text-accent-600"></i>
              <span className="text-xs text-accent-700 font-medium">MORE RESOURCES</span>
            </div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              Sugargoo Shopping Guides & Resources
            </h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
              In-depth articles covering everything from product discovery to shipping strategies and money-saving tips.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {[
              {
                image: 'https://readdy.ai/api/search-image?query=Stylish%20pair%20of%20premium%20white%20leather%20sneakers%20placed%20on%20a%20warm%20cream%20colored%20surface%20with%20soft%20natural%20window%20light%20creating%20gentle%20shadows%20editorial%20product%20photography%20clean%20minimal%20composition%20subtle%20reflections%20on%20polished%20surface%20warm%20neutral%20tones&width=600&height=400&seq=seocard-sneaker-01&orientation=landscape',
                tag: 'Shopping Guide',
                title: 'Complete Sugargoo Spreadsheet Database: Find Every Product You Need',
                desc: 'Master the art of organized product discovery with curated spreadsheets covering all fourteen categories.',
                readTime: '12 min read',
                link: '/article/sugargoo-spreadsheet-guide',
              },
              {
                image: 'https://readdy.ai/api/search-image?query=Elegant%20minimalist%20desk%20setup%20with%20a%20notebook%20pen%20and%20a%20small%20potted%20plant%20on%20a%20warm%20beige%20surface%20soft%20natural%20daylight%20from%20a%20nearby%20window%20creating%20a%20calm%20productive%20atmosphere%20editorial%20product%20photography%20refined%20aesthetic%20clean%20composition&width=600&height=400&seq=seocard-review-02&orientation=landscape',
                tag: 'Review',
                title: 'Sugargoo Review 2026: Is Sugargoo Legit? Honest Platform Analysis',
                desc: 'Independent review covering warehouse services, QC inspections, shipping reliability and customer support quality.',
                readTime: '10 min read',
                link: '/article/sugargoo-review',
              },
              {
                image: 'https://readdy.ai/api/search-image?query=Neatly%20packaged%20brown%20cardboard%20shipping%20box%20with%20packing%20tape%20and%20a%20shipping%20label%20on%20a%20clean%20cream%20colored%20surface%20soft%20natural%20side%20lighting%20editorial%20product%20photography%20minimal%20composition%20warm%20neutral%20tones%20sense%20of%20anticipation%20and%20delivery&width=600&height=400&seq=seocard-ship-03&orientation=landscape',
                tag: 'Shipping',
                title: 'Sugargoo Shipping Cost Guide: Save on International Delivery',
                desc: 'Compare shipping lines, understand cost factors and learn proven strategies to reduce your shipping expenses.',
                readTime: '9 min read',
                link: '/article/sugargoo-shipping-guide',
              },
              {
                image: 'https://readdy.ai/api/search-image?query=A%20golden%20discount%20tag%20and%20a%20few%20elegant%20coins%20scattered%20on%20a%20warm%20cream%20colored%20surface%20soft%20natural%20daylight%20clean%20minimal%20product%20photography%20editorial%20style%20subtle%20metallic%20reflections%20warm%20neutral%20aesthetic%20sense%20of%20savings%20and%20value&width=600&height=400&seq=seocard-coupon-04&orientation=landscape',
                tag: 'Savings',
                title: 'Sugargoo Coupon Codes 2026: Discounts, Promos & Deals Guide',
                desc: 'Discover working coupon strategies, seasonal promotions and loyalty rewards to maximize your savings.',
                readTime: '8 min read',
                link: '/article/sugargoo-coupon-guide',
              },
              {
                image: 'https://readdy.ai/api/search-image?query=Curated%20collection%20of%20fashion%20items%20including%20a%20folded%20hoodie%20a%20pair%20of%20sunglasses%20and%20a%20sleek%20watch%20arranged%20beautifully%20on%20a%20warm%20sand%20colored%20surface%20soft%20natural%20daylight%20editorial%20product%20photography%20harmonious%20composition%20neutral%20warm%20palette&width=600&height=400&seq=seocard-haul-05&orientation=landscape',
                tag: 'Tips',
                title: 'How to Build the Perfect Sugargoo Haul: Step-by-Step Strategy',
                desc: 'Learn how to plan, organize and execute your first Sugargoo haul with confidence and efficiency.',
                readTime: '11 min read',
                link: '/article/sugargoo-spreadsheet-guide',
              },
              {
                image: 'https://readdy.ai/api/search-image?query=Professional%20camera%20and%20a%20magnifying%20glass%20on%20a%20clean%20cream%20colored%20desk%20surface%20with%20soft%20natural%20lighting%20from%20a%20nearby%20window%20editorial%20product%20photography%20clean%20minimal%20composition%20warm%20neutral%20tones%20sense%20of%20detailed%20inspection%20and%20quality%20checking&width=600&height=400&seq=seocard-qc-06&orientation=landscape',
                tag: 'QC Guide',
                title: 'Sugargoo QC Photo Guide: How to Inspect Products Before Shipping',
                desc: 'Understand what to look for in QC photos, how to spot defects and when to request additional inspection.',
                readTime: '7 min read',
                link: '/article/sugargoo-review',
              },
              {
                image: 'https://readdy.ai/api/search-image?query=Two%20contrasting%20fashion%20items%20a%20sleek%20black%20leather%20bag%20and%20a%20casual%20canvas%20tote%20side%20by%20side%20on%20a%20warm%20cream%20colored%20surface%20soft%20natural%20daylight%20editorial%20product%20photography%20clean%20composition%20subtle%20comparison%20aesthetic%20neutral%20warm%20tones&width=600&height=400&seq=seocard-compare-07&orientation=landscape',
                tag: 'Comparison',
                title: 'Sugargoo vs Other Shopping Agents: Which Platform is Best?',
                desc: 'Honest comparison of Sugargoo against other popular agent platforms on pricing, features and reliability.',
                readTime: '9 min read',
                link: '/article/sugargoo-review',
              },
              {
                image: 'https://readdy.ai/api/search-image?query=Open%20laptop%20displaying%20a%20clean%20spreadsheet%20interface%20next%20to%20a%20cup%20of%20coffee%20on%20a%20warm%20wooden%20desk%20soft%20natural%20daylight%20streaming%20from%20a%20window%20editorial%20lifestyle%20photography%20modern%20productive%20atmosphere%20warm%20neutral%20tones%20clean%20composition&width=600&height=400&seq=seocard-beginner-08&orientation=landscape',
                tag: 'Tutorial',
                title: 'Sugargoo Beginner Tutorial: From Sign-Up to First Order',
                desc: 'Complete walkthrough for newcomers covering account setup, product searching and placing your first order.',
                readTime: '10 min read',
                link: '/article/sugargoo-spreadsheet-guide',
              },
              {
                image: 'https://readdy.ai/api/search-image?query=Beautiful%20arrangement%20of%20stylish%20accessories%20including%20a%20classic%20watch%20a%20leather%20bracelet%20and%20designer%20inspired%20sunglasses%20on%20a%20warm%20sand%20colored%20surface%20soft%20natural%20daylight%20editorial%20product%20photography%20elegant%20composition%20warm%20neutral%20palette%20refined%20aesthetic&width=600&height=400&seq=seocard-finds-09&orientation=landscape',
                tag: 'Curated',
                title: 'Best Sugargoo Finds 2026: Top-Rated Products Across All Categories',
                desc: 'Our picks for the highest-rated and most popular product discoveries in the Sugargoo community this year.',
                readTime: '11 min read',
                link: '/article/sugargoo-coupon-guide',
              },
            ].map((card, i) => (
              <Link
                key={i}
                to={card.link}
                className="group flex flex-col rounded-xl bg-background-50 border border-background-200/60 hover:border-primary-300 transition-all duration-200 cursor-pointer overflow-hidden"
              >
                <div className="w-full h-44 md:h-48 overflow-hidden">
                  <img
                    src={card.image}
                    alt={card.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                </div>
                <div className="flex flex-col gap-2 p-4 md:p-5 flex-1">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-primary-600 font-medium bg-primary-100 px-2.5 py-0.5 rounded-full">{card.tag}</span>
                    <span className="text-xs text-foreground-400">{card.readTime}</span>
                  </div>
                  <h3 className="text-sm md:text-base font-semibold text-foreground-900 leading-snug group-hover:text-primary-600 transition-colors line-clamp-2">
                    {card.title}
                  </h3>
                  <p className="text-xs text-foreground-600 leading-relaxed line-clamp-2 flex-1">
                    {card.desc}
                  </p>
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-primary-500 group-hover:text-primary-600 transition-colors mt-1">
                    Read More <i className="ri-arrow-right-line text-xs"></i>
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative w-full px-6 md:px-10 py-20 md:py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Stylized%20editorial%20composition%20of%20curated%20fashion%20products%20including%20accessories%20watches%20leather%20goods%20and%20textiles%20arranged%20artistically%20on%20a%20warm%20beige%20textured%20surface%20soft%20natural%20light%20refined%20product%20photography%20elegant%20neutral%20aesthetic%20modern%20minimal%20style%20subtle%20organic%20patterns&width=1600&height=800&seq=cta-bg-v2-2026&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/55"></div>
        </div>
        <div className="relative z-10 max-w-2xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-4">
            Ready to Explore the Best Sugargoo Finds?
          </h2>
          <p className="text-sm md:text-base text-background-50/80 mb-8 max-w-lg mx-auto leading-relaxed">
            Jump into our curated spreadsheet with thousands of verified products across 14 categories.
            Updated regularly with fresh finds.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="w-full sm:w-auto px-8 py-3.5 bg-accent-500 text-foreground-950 rounded-full font-semibold text-sm hover:bg-accent-400 transition-all duration-200 whitespace-nowrap cursor-pointer shadow-lg shadow-accent-500/20"
            >
              Sugargoo Spreadsheet
              <i className="ri-arrow-right-up-line ml-2"></i>
            </a>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="w-full sm:w-auto px-8 py-3.5 bg-background-50/15 backdrop-blur-sm border border-background-50/30 text-background-50 rounded-full font-semibold text-sm hover:bg-background-50/25 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Buy It Now
              <i className="ri-shopping-cart-line ml-2"></i>
            </a>
          </div>
        </div>
      </section>

      <Footer />

      {/* Schema.org WebSite structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: 'SugargooSheet Study',
            url: import.meta.env.VITE_SITE_URL,
            description: 'The ultimate independent Sugargoo Spreadsheet resource helping international shoppers discover and organize products.',
            potentialAction: {
              '@type': 'SearchAction',
              target: import.meta.env.VITE_SITE_URL + '/search?q={search_term_string}',
              'query-input': 'required name=search_term_string',
            },
          }),
        }}
      />
    </>
  );
}